// src/pages/ArabicHangman.js
import React, { useState, useEffect, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { Home, RotateCcw, Trophy, Star } from 'lucide-react';
import useAudio from '../../hooks/useAudio';
import useFirebase from '../../hooks/useFirebase';
import { Link } from 'react-router-dom';
import useAnalytics from '../../hooks/useAnalytics';

// Hangman stages - ASCII art for the gallows
const HANGMAN_STAGES = [
  // Stage 0 - Empty gallows
  <svg key="stage-0" width="200" height="200" viewBox="0 0 200 200" className="text-brown-600">
    <line x1="20" y1="180" x2="180" y2="180" stroke="brown" strokeWidth="4" />
    <line x1="60" y1="180" x2="60" y2="20" stroke="brown" strokeWidth="4" />
    <line x1="60" y1="20" x2="140" y2="20" stroke="brown" strokeWidth="4" />
    <line x1="140" y1="20" x2="140" y2="40" stroke="brown" strokeWidth="4" />
  </svg>,
  // Stage 1 - Head
  <svg key="stage-1" width="200" height="200" viewBox="0 0 200 200" className="text-brown-600">
    <line x1="20" y1="180" x2="180" y2="180" stroke="brown" strokeWidth="4" />
    <line x1="60" y1="180" x2="60" y2="20" stroke="brown" strokeWidth="4" />
    <line x1="60" y1="20" x2="140" y2="20" stroke="brown" strokeWidth="4" />
    <line x1="140" y1="20" x2="140" y2="40" stroke="brown" strokeWidth="4" />
    <circle cx="140" cy="60" r="20" stroke="black" strokeWidth="2" fill="none" />
  </svg>,
  // Stage 2 - Body
  <svg key="stage-2" width="200" height="200" viewBox="0 0 200 200" className="text-brown-600">
    <line x1="20" y1="180" x2="180" y2="180" stroke="brown" strokeWidth="4" />
    <line x1="60" y1="180" x2="60" y2="20" stroke="brown" strokeWidth="4" />
    <line x1="60" y1="20" x2="140" y2="20" stroke="brown" strokeWidth="4" />
    <line x1="140" y1="20" x2="140" y2="40" stroke="brown" strokeWidth="4" />
    <circle cx="140" cy="60" r="20" stroke="black" strokeWidth="2" fill="none" />
    <line x1="140" y1="80" x2="140" y2="120" stroke="black" strokeWidth="2" />
  </svg>,
  // Stage 3 - Left arm
  <svg key="stage-3" width="200" height="200" viewBox="0 0 200 200" className="text-brown-600">
    <line x1="20" y1="180" x2="180" y2="180" stroke="brown" strokeWidth="4" />
    <line x1="60" y1="180" x2="60" y2="20" stroke="brown" strokeWidth="4" />
    <line x1="60" y1="20" x2="140" y2="20" stroke="brown" strokeWidth="4" />
    <line x1="140" y1="20" x2="140" y2="40" stroke="brown" strokeWidth="4" />
    <circle cx="140" cy="60" r="20" stroke="black" strokeWidth="2" fill="none" />
    <line x1="140" y1="80" x2="140" y2="120" stroke="black" strokeWidth="2" />
    <line x1="140" y1="90" x2="120" y2="80" stroke="black" strokeWidth="2" />
  </svg>,
  // Stage 4 - Right arm
  <svg key="stage-4" width="200" height="200" viewBox="0 0 200 200" className="text-brown-600">
    <line x1="20" y1="180" x2="180" y2="180" stroke="brown" strokeWidth="4" />
    <line x1="60" y1="180" x2="60" y2="20" stroke="brown" strokeWidth="4" />
    <line x1="60" y1="20" x2="140" y2="20" stroke="brown" strokeWidth="4" />
    <line x1="140" y1="20" x2="140" y2="40" stroke="brown" strokeWidth="4" />
    <circle cx="140" cy="60" r="20" stroke="black" strokeWidth="2" fill="none" />
    <line x1="140" y1="80" x2="140" y2="120" stroke="black" strokeWidth="2" />
    <line x1="140" y1="90" x2="120" y2="80" stroke="black" strokeWidth="2" />
    <line x1="140" y1="90" x2="160" y2="80" stroke="black" strokeWidth="2" />
  </svg>,
  // Stage 5 - Left leg
  <svg key="stage-5" width="200" height="200" viewBox="0 0 200 200" className="text-brown-600">
    <line x1="20" y1="180" x2="180" y2="180" stroke="brown" strokeWidth="4" />
    <line x1="60" y1="180" x2="60" y2="20" stroke="brown" strokeWidth="4" />
    <line x1="60" y1="20" x2="140" y2="20" stroke="brown" strokeWidth="4" />
    <line x1="140" y1="20" x2="140" y2="40" stroke="brown" strokeWidth="4" />
    <circle cx="140" cy="60" r="20" stroke="black" strokeWidth="2" fill="none" />
    <line x1="140" y1="80" x2="140" y2="120" stroke="black" strokeWidth="2" />
    <line x1="140" y1="90" x2="120" y2="80" stroke="black" strokeWidth="2" />
    <line x1="140" y1="90" x2="160" y2="80" stroke="black" strokeWidth="2" />
    <line x1="140" y1="120" x2="120" y2="140" stroke="black" strokeWidth="2" />
  </svg>,
  // Stage 6 - Right leg (complete)
  <svg key="stage-6" width="200" height="200" viewBox="0 0 200 200" className="text-brown-600">
    <line x1="20" y1="180" x2="180" y2="180" stroke="brown" strokeWidth="4" />
    <line x1="60" y1="180" x2="60" y2="20" stroke="brown" strokeWidth="4" />
    <line x1="60" y1="20" x2="140" y2="20" stroke="brown" strokeWidth="4" />
    <line x1="140" y1="20" x2="140" y2="40" stroke="brown" strokeWidth="4" />
    <circle cx="140" cy="60" r="20" stroke="black" strokeWidth="2" fill="none" />
    <line x1="140" y1="80" x2="140" y2="120" stroke="black" strokeWidth="2" />
    <line x1="140" y1="90" x2="120" y2="80" stroke="black" strokeWidth="2" />
    <line x1="140" y1="90" x2="160" y2="80" stroke="black" strokeWidth="2" />
    <line x1="140" y1="120" x2="120" y2="140" stroke="black" strokeWidth="2" />
    <line x1="140" y1="120" x2="160" y2="140" stroke="black" strokeWidth="2" />
  </svg>
];

const SCORING_CONSTANTS = {
  BASE_POINTS: 100,
  SPEED_BONUS_MAX: 50,
  ACCURACY_BONUS_MAX: 100,
  PERFECT_ACCURACY_BONUS: 150,
  IDEAL_TIME_PER_QUESTION: 8,
  MAX_TIME_BONUS: 50,
  STREAK_BONUS: 25,
  STAR_THRESHOLDS: {
    THREE: 85,
    TWO: 70,
    ONE: 50
  }
};

const checkLetterInWord = (guessedLetter, arabicWord) => {
  const letterGroups = {
    'ا': ['ا', 'أ', 'إ', 'آ'],
    'أ': ['ا', 'أ', 'إ', 'آ'],
    'إ': ['ا', 'أ', 'إ', 'آ'], 
    'آ': ['ا', 'أ', 'إ', 'آ'],
    'ي': ['ي', 'ى'],
    'ى': ['ي', 'ى'],
    'ت': ['ت', 'ة'],
    'ة': ['ت', 'ة'],
    'ه': ['ه', 'ة'],
    'ء': ['ء', 'ؤ', 'ئ'],
    'ؤ': ['ء', 'ؤ', 'ئ'],
    'ئ': ['ء', 'ؤ', 'ئ'],
  };

  const lettersToCheck = letterGroups[guessedLetter] || [guessedLetter];
  return lettersToCheck.some(letter => arabicWord.includes(letter));
};

const ArabicHangman = () => {
  // Initialize hooks
  const { saveSessionData } = useAnalytics();
  const { 
    userId,
    level,
    setLevel,
    maxLevel,
    levelsData,
    bestScores,
    isLoading,
    saveLevelData,
    updateLevel,
    unlockNextLevel,
    resetAllProgress
  } = useFirebase('ArabicHangman');

  const {
    playCorrectSound,
    playIncorrectSound,
    playPerfectSound,
    playStreakSound,
    playWhooshSound,
    playStarSound,
    startBackgroundMusic,
    AudioElements
  } = useAudio();

  // Game state
  const [gamePhase, setGamePhase] = useState('menu'); // 'menu', 'playing', 'complete'
  const [currentLevelWords, setCurrentLevelWords] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentWord, setCurrentWord] = useState(null);
  const [allLevelWords, setAllLevelWords] = useState([]);
  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [sessionMistakes, setSessionMistakes] = useState([]);
  
  // Hangman state
  const [guessedLetters, setGuessedLetters] = useState([]);
  const [wrongGuesses, setWrongGuesses] = useState(0);
  const [gameStatus, setGameStatus] = useState('playing'); // 'playing', 'won', 'lost'
  
  // Enhanced scoring system
  const [totalScore, setTotalScore] = useState(0);
  const [currentAttemptScore, setCurrentAttemptScore] = useState(0);
  const [timeBonus, setTimeBonus] = useState(0);
  const [streakBonus, setStreakBonus] = useState(0);
  const [accuracyBonus, setAccuracyBonus] = useState(0);
  const [stars, setStars] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [correctAttempts, setCorrectAttempts] = useState(0);
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [levelStartTime, setLevelStartTime] = useState(Date.now());
  const [currentQuestionStartTime, setCurrentQuestionStartTime] = useState(Date.now());
  
  // UI state
  const [feedback, setFeedback] = useState('');
  const [celebrations, setCelebrations] = useState({});
  const [sparkles, setSparkles] = useState({});
  const [isLoadingLevel, setIsLoadingLevel] = useState(true);
  const [showScoringHelp, setShowScoringHelp] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [levelCompleteData, setLevelCompleteData] = useState(null);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);

  // Utility functions
  const shuffleArray = useCallback((array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }, []);

  const isLetterGuessed = (wordLetter, guessedLetters) => {
    const letterGroups = {
        'ا': ['ا', 'أ', 'إ', 'آ'],
        'أ': ['ا', 'أ', 'إ', 'آ'],
        'إ': ['ا', 'أ', 'إ', 'آ'], 
        'آ': ['ا', 'أ', 'إ', 'آ'],
        'ي': ['ي', 'ى'],
        'ى': ['ي', 'ى'],
        'ت': ['ت', 'ة'],
        'ة': ['ت', 'ة'],
        'ه': ['ه', 'ة'],
        'ء': ['ء', 'ؤ', 'ئ'],
        'ؤ': ['ء', 'ؤ', 'ئ'],
        'ئ': ['ء', 'ؤ', 'ئ'],
    };

    return guessedLetters.some(guessedLetter => {
        const guessedGroup = letterGroups[guessedLetter] || [guessedLetter];
        return guessedGroup.includes(wordLetter);
    });
  };

  const getDisplayedWord = useCallback(() => {
    if (!currentWord?.arabic) return '';

    return currentWord.arabic.split('').map(char => {
        if (isLetterGuessed(char, guessedLetters) || char === ' ' || char === 'ـ') {
        return char;
        }
        return '_';
    }).join(' ');
  }, [currentWord, guessedLetters]);

  const isWordGuessed = useCallback(() => {
    if (!currentWord?.arabic) return false;
    
    return currentWord.arabic.split('').every(char => 
        isLetterGuessed(char, guessedLetters) || char === ' ' || char === 'ـ'
    );
  }, [currentWord, guessedLetters]);

  const resetLevelState = useCallback(() => {
    setCurrentWordIndex(0);
    setCurrentWord(null);
    setGuessedLetters([]);
    setWrongGuesses(0);
    setGameStatus('playing');
    setFeedback('');
    setTotalScore(0);
    setCurrentAttemptScore(0);
    setTimeBonus(0);
    setStreakBonus(0);
    setAccuracyBonus(0);
    setStars(0);
    setAttempts(0);
    setCorrectAttempts(0);
    setConsecutiveCorrect(0);
    setLevelStartTime(Date.now());
    setCurrentQuestionStartTime(Date.now());
    setCelebrations({});
    setSparkles({});
    setShowLevelComplete(false);
    setLevelCompleteData(null);
  }, []);

  const calculateTimeBonus = useCallback((startTime, totalWords) => {
    const timeSpent = (Date.now() - startTime) / 1000;
    const idealTime = totalWords * SCORING_CONSTANTS.IDEAL_TIME_PER_QUESTION;
    const bonus = Math.max(0, Math.min(
      SCORING_CONSTANTS.MAX_TIME_BONUS, 
      idealTime - timeSpent + 20
    ));
    return Math.round(bonus);
  }, []);

  const calculateAccuracyBonus = useCallback((correct, total) => {
    const accuracy = total > 0 ? (correct / total) * 100 : 0;
    if (accuracy === 100) return SCORING_CONSTANTS.PERFECT_ACCURACY_BONUS;
    return Math.round((accuracy / 100) * SCORING_CONSTANTS.ACCURACY_BONUS_MAX);
  }, []);

  const calculateStars = useCallback((score, totalWords, accuracy) => {
    const maxPossibleScore = totalWords * SCORING_CONSTANTS.BASE_POINTS + 
      SCORING_CONSTANTS.MAX_TIME_BONUS + SCORING_CONSTANTS.ACCURACY_BONUS_MAX;
    const scorePercentage = (score / maxPossibleScore) * 100;
    
    if (accuracy === 100 && scorePercentage >= 90) return 3;
    if (accuracy >= 85 && scorePercentage >= 70) return 2;
    if (accuracy >= 60 && scorePercentage >= 50) return 1;
    return 0;
  }, []);

  const triggerCelebration = useCallback((isStreak = false, starCount = 0) => {
    const id = Date.now();
    setCelebrations(prev => ({ ...prev, [id]: true }));
    
    if (starCount > 0) {
      Array.from({ length: starCount }, (_, i) => {
        setTimeout(() => playStarSound(), i * 200);
      });
    }
    
    if (isStreak) {
      playStreakSound();
      setSparkles(prev => ({ ...prev, [id]: true }));
      setTimeout(() => {
        setSparkles(prev => {
          const newSparkles = { ...prev };
          delete newSparkles[id];
          return newSparkles;
        });
      }, 2000);
    }
    
    setTimeout(() => {
      setCelebrations(prev => {
        const newCelebrations = { ...prev };
        delete newCelebrations[id];
        return newCelebrations;
      });
    }, 1500);
  }, [playStarSound, playStreakSound]);

  const startLevel = useCallback(() => {
    setSessionStartTime(Date.now()); 
    setSessionMistakes([]);
    setGamePhase('playing');
    resetLevelState();
  }, [resetLevelState]);

  const goToMenu = useCallback(() => {
    setGamePhase('menu');
  }, []);

  const selectLevel = useCallback((newLevel) => {
    updateLevel(newLevel);
  }, [updateLevel]);

  const setupCurrentWord = useCallback(() => {
    if (!currentLevelWords.length || currentWordIndex >= currentLevelWords.length) return;
    
    const word = currentLevelWords[currentWordIndex];
    setCurrentWord(word);
    setGuessedLetters([]);
    setWrongGuesses(0);
    setGameStatus('playing');
    setFeedback('');
    setCurrentQuestionStartTime(Date.now());
  }, [currentLevelWords, currentWordIndex]);

  const handleLetterGuess = useCallback((letter) => {
    if (gameStatus !== 'playing' || guessedLetters.includes(letter)) return;

    setGuessedLetters(prev => [...prev, letter]);
    setAttempts(prev => prev + 1);

    const isCorrect = checkLetterInWord(letter, currentWord.arabic);
    
    if (isCorrect) {
      setCorrectAttempts(prev => prev + 1);
      setFeedback('correct');
      playCorrectSound();
      
      // Check if word is complete
      const newGuesses = [...guessedLetters, letter];
      const wordComplete = currentWord.arabic.split('').every(char => 
        isLetterGuessed(char, newGuesses) || char === ' ' || char === 'ـ'
      );
      
      if (wordComplete) {
        setGameStatus('won');
        setConsecutiveCorrect(prev => prev + 1);
        
        let points = SCORING_CONSTANTS.BASE_POINTS;
        const responseTime = Date.now() - currentQuestionStartTime;
        const speedBonus = Math.max(0, SCORING_CONSTANTS.SPEED_BONUS_MAX - Math.floor(responseTime / 100));
        points += speedBonus;
        
        if (consecutiveCorrect >= 2) {
          const bonus = SCORING_CONSTANTS.STREAK_BONUS;
          setStreakBonus(prev => prev + bonus);
          points += bonus;
          triggerCelebration(true);
        } else {
          triggerCelebration();
        }
        
        setTotalScore(prev => prev + points);
        setCurrentAttemptScore(prev => prev + points);
      }
    } else {
      setSessionMistakes(prev => [...prev, {
        timestamp: Date.now(),
        type: 'wrong_letter',
        letter: letter,
        word: currentWord.word,
        wordIndex: currentWordIndex
      }]);
      setWrongGuesses(prev => {
        const newWrong = prev + 1;
        if (newWrong >= 6) {
          setGameStatus('lost');
          setConsecutiveCorrect(0);
        }
        return newWrong;
      });
      setFeedback('incorrect');
      playIncorrectSound();
    }
  }, [gameStatus, guessedLetters, currentWord, consecutiveCorrect, currentQuestionStartTime, triggerCelebration, playCorrectSound, playIncorrectSound]);

  const handleNextWord = useCallback(() => {
    if (gameStatus === 'playing') return;

    playWhooshSound();
    
    setCurrentWordIndex(prev => {
      const newIndex = prev + 1;
      if (newIndex >= currentLevelWords.length) {
        handleLevelComplete();
        return prev;
      }
      return newIndex;
    });
  }, [gameStatus, currentLevelWords.length, playWhooshSound]);

  const handleLevelComplete = useCallback(() => {
    if (!currentLevelWords.length) return;

    // Calculate final scores
    const finalTimeBonus = calculateTimeBonus(levelStartTime, currentLevelWords.length);
    const accuracy = Math.round((correctAttempts / Math.max(1, attempts)) * 100);
    const finalAccuracyBonus = calculateAccuracyBonus(correctAttempts, attempts);
    const finalTotalScore = totalScore + finalTimeBonus + finalAccuracyBonus;
    const earnedStars = calculateStars(finalTotalScore, currentLevelWords.length, accuracy);

    // Update displayed scores
    setTimeBonus(finalTimeBonus);
    setAccuracyBonus(finalAccuracyBonus);
    setStars(earnedStars);
    setCurrentAttemptScore(finalTotalScore);

    const scoreData = {
      totalScore: finalTotalScore,
      stars: earnedStars,
      accuracy,
      timeBonus: finalTimeBonus,
      streakBonus,
      attempts
    };

    // Save the level data
    saveLevelData(level, scoreData);
    const sessionData = {
      gameType: 'arabicHangman',
      level,
      sessionStartTime,
      sessionDuration: Math.round((Date.now() - sessionStartTime) / 1000),
      totalScore: finalTotalScore,
      accuracy,
      attempts,
      correctAnswers: correctAttempts,
      mistakes: sessionMistakes,
      starsEarned: earnedStars,
      timeBonus: finalTimeBonus,
      streakBonus
    };
    saveSessionData(sessionData);

    // Prepare completion data
    const nextLevel = level + 1;
    const isLastLevel = !levelsData?.[nextLevel.toString()];
    
    // Unlock next level immediately when level is completed
    if (!isLastLevel && accuracy >= 70) {
      console.log('Unlocking next level with accuracy:', accuracy);
      unlockNextLevel();
    }
    
    setLevelCompleteData({
      ...scoreData,
      currentLevel: level,
      nextLevel,
      isLastLevel,
      levelTitle: levelsData[level.toString()]?.title || `Level ${level}`
    });

    // Show completion effects
    if (earnedStars > 0) {
      triggerCelebration(false, earnedStars);
    }

    playPerfectSound();
    confetti({
      particleCount: 200,
      spread: 100,
      origin: { y: 0.5 },
      colors: ['#FFD700', '#FFA500', '#FF6347'],
      shapes: ['star', 'circle']
    });
    
    setShowLevelComplete(true);
  }, [
    currentLevelWords.length,
    levelStartTime,
    correctAttempts,
    attempts,
    totalScore,
    streakBonus,
    level,
    levelsData,
    calculateTimeBonus,
    calculateAccuracyBonus,
    calculateStars,
    saveLevelData,
    unlockNextLevel,
    triggerCelebration,
    playPerfectSound
  ]);

  const handleProceedToNextLevel = useCallback(() => {
    if (!levelCompleteData) return;
    
    if (levelCompleteData.isLastLevel) {
      confetti({
        particleCount: 300,
        spread: 160,
        origin: { y: 0.4 },
        colors: ['#FFD700', '#FFA500', '#FF69B4', '#00CED1', '#32CD32'],
        shapes: ['star'],
        gravity: 0.6,
        drift: 0.2
      });
      alert("🎉 You've completed all levels! Amazing job!");
    } else {
      updateLevel(levelCompleteData.nextLevel);
    }
    
    setShowLevelComplete(false);
    setLevelCompleteData(null);
    setGamePhase('menu');
  }, [levelCompleteData, updateLevel]);

  const handleResetProgress = useCallback(() => {
    setShowResetConfirmation(true);
  }, []);

  const confirmResetProgress = useCallback(() => {
    resetAllProgress();
    resetLevelState();
    updateLevel(1);
    setShowResetConfirmation(false);
    setGamePhase('menu');
    playWhooshSound();
  }, [resetAllProgress, resetLevelState, updateLevel, playWhooshSound]);

  // Initialize level data when component mounts or level changes
  useEffect(() => {
    if (!levelsData || !level || !levelsData[level.toString()]) {
      setIsLoadingLevel(false);
      return;
    }
    
    const levelData = levelsData[level.toString()];
    if (!levelData?.wordPairs) {
      setIsLoadingLevel(false);
      return;
    }

    setIsLoadingLevel(true);
    
    try {
      const transformedWords = levelData.wordPairs.map(pair => ({
        english: pair.english,
        arabic: pair.arabic,
        pronunciation: pair.pronunciation,
        emoji: pair.emoji
      }));

      const shuffledWords = shuffleArray([...transformedWords]);
      setCurrentLevelWords(shuffledWords);
      
      const allWords = [];
      Object.values(levelsData).forEach(lvl => {
        if (lvl.wordPairs) {
          allWords.push(...lvl.wordPairs.map(pair => ({
            english: pair.english,
            arabic: pair.arabic
          })));
        }
      });
      setAllLevelWords(allWords);
      
    } catch (error) {
      console.error('Error initializing level data:', error);
    } finally {
      setIsLoadingLevel(false);
    }
  }, [levelsData, level, shuffleArray]);

  // Set up current word when word index changes
  useEffect(() => {
    if (isLoadingLevel || gamePhase !== 'playing') return;
    
    const dataReady = currentLevelWords.length > 0 && 
                     currentWordIndex < currentLevelWords.length;

    if (dataReady) {
      setupCurrentWord();
    }
  }, [currentWordIndex, currentLevelWords, setupCurrentWord, isLoadingLevel, gamePhase]);

  // Start background music when component mounts
  useEffect(() => {
    if (userId) {
      setTimeout(startBackgroundMusic, 1000);
    }
  }, [userId, startBackgroundMusic]);

  // Letter Button Component
  const LetterButton = ({ letter, onClick, disabled }) => (
    <button
      onClick={() => onClick(letter)}
      disabled={disabled}
      className={`
        p-2 md:p-3 rounded-xl font-bold text-lg md:text-xl transition-all duration-200 border-2
        ${disabled 
          ? 'bg-gray-200 text-gray-400 cursor-not-allowed border-gray-300' 
          : 'bg-gradient-to-r from-blue-100 to-purple-100 text-purple-800 border-purple-300 hover:from-blue-200 hover:to-purple-200 hover:shadow-lg transform hover:scale-105 cursor-pointer'
        }
      `}
      dir="rtl"
      lang="ar"
    >
      {letter}
    </button>
  );

  // Star Display Component
  const StarDisplay = React.memo(({ count, size = "text-2xl" }) => {
    return (
      <div className={`flex justify-center gap-1 ${size}`}>
        {[1, 2, 3].map(star => (
          <span 
            key={star} 
            className={`${star <= count ? 'text-yellow-400' : 'text-gray-300'} drop-shadow-lg`}
          >
            ⭐
          </span>
        ))}
      </div>
    );
  });

  // Loading state
  if (isLoading || isLoadingLevel) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full border-4 border-yellow-300">
          <div className="text-center">
            <div className="text-4xl md:text-6xl mb-4 animate-spin">🎭</div>
            <div className="text-purple-600 text-xl md:text-3xl font-bold animate-pulse">Loading Hangman...</div>
            <div className="text-gray-600 text-sm md:text-lg mt-2">Getting ready for word guessing!</div>
          </div>
        </div>
      </div>
    );
  }

  if (!levelsData || !levelsData[level?.toString()]) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-md w-full border-4 border-red-300">
          <div className="text-center">
            <div className="text-6xl mb-4">😕</div>
            <div className="text-red-600 text-xl md:text-2xl font-bold mb-4">No level data found!</div>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              🔄 Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentLevelData = levelsData[level.toString()];
  const accuracy = attempts > 0 ? Math.round((correctAttempts / attempts) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 p-4">
      {/* Audio Elements */}
      <AudioElements />
      
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4 md:mb-8">
          <h1 className="text-3xl md:text-6xl font-black text-white mb-2 md:mb-4 drop-shadow-2xl">
            🎭 Arabic Hangman 🎭
          </h1>
          <p className="text-lg md:text-2xl text-white font-bold opacity-90 drop-shadow-lg">
            Guess the Arabic words letter by letter!
          </p>
        </div>

        {gamePhase === 'menu' && (
          <div className="max-w-4xl mx-auto">
            {/* Level Info */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 mb-4 md:mb-8 border-4 border-yellow-300">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-2 md:mb-4 text-purple-600">
                🎮 Level {level}: {currentLevelData.title || `Level ${level}`}
              </h2>
              <p className="text-center text-gray-700 mb-4 md:mb-6 text-lg md:text-xl font-semibold">
                Guess the Arabic words before the hangman is complete!
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6 mb-4 md:mb-8">
                <div className="text-center bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🎯</div>
                  <p className="font-bold text-sm md:text-lg">Words</p>
                  <p className="text-lg md:text-2xl font-black">{currentLevelWords.length}</p>
                </div>
                <div className="text-center bg-gradient-to-br from-green-400 to-green-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🎭</div>
                  <p className="font-bold text-sm md:text-lg">Game Type</p>
                  <p className="text-lg md:text-2xl font-black">Hangman</p>
                </div>
                <div className="text-center bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🏆</div>
                  <p className="font-bold text-sm md:text-lg">Pass</p>
                  <p className="text-lg md:text-2xl font-black">70%</p>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={startLevel}
                  className="px-8 md:px-12 py-4 md:py-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-3xl font-black text-xl md:text-3xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300 border-4 border-white"
                >
                  🚀 START GUESSING! 🚀
                </button>
              </div>
            </div>

            {/* Level Grid - FIXED: Show ALL levels (locked and unlocked) */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 border-4 border-pink-300">
              <h3 className="text-xl md:text-3xl font-bold text-center mb-4 md:mb-6 text-purple-600">
                🎮 Choose Your Level!
              </h3>
              <div className="grid grid-cols-4 md:grid-cols-7 gap-2 md:gap-4">
                {/* FIXED: Show ALL available levels, not just unlocked ones */}
                {Array.from({ length: Math.max(Object.keys(levelsData).length, maxLevel) }, (_, i) => i + 1).map(levelNum => {
                  const isUnlocked = levelNum <= maxLevel;
                  const isCurrent = levelNum === level;
                  const levelScore = bestScores[`level${levelNum}`];
                  
                  return (
                    <button
                      key={levelNum}
                      onClick={() => isUnlocked ? selectLevel(levelNum) : null}
                      disabled={!isUnlocked}
                      className={`
                        relative p-2 md:p-4 rounded-2xl text-sm md:text-lg font-bold transition-all duration-300 min-h-[80px] md:min-h-[120px] flex flex-col items-center justify-center border-2 md:border-4
                        ${isCurrent 
                          ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-2xl transform scale-105 md:scale-110 border-yellow-300' 
                          : isUnlocked 
                            ? 'bg-gradient-to-br from-green-100 to-green-200 text-green-800 hover:from-green-200 hover:to-green-300 cursor-pointer hover:scale-105 border-green-300' 
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed border-gray-300'
                        }
                      `}
                      title={isUnlocked ? `Level ${levelNum}${levelScore ? ` (Best: ${levelScore.bestScore || levelScore.totalScore || 0})` : ''}` : 'Locked'}
                    >
                      <div className="text-lg md:text-2xl font-black">{levelNum}</div>
                      <div className="text-xs mt-1 font-bold hidden md:block">Hangman</div>
                      <div className="text-xs font-bold md:hidden">Hangman</div>
                      {levelScore && (
                        <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2">
                          <StarDisplay count={levelScore.stars || 0} size="text-xs md:text-sm" />
                        </div>
                      )}
                      {!isUnlocked && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xl md:text-3xl">🔒</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              
              <div className="mt-4 md:mt-8 flex gap-2 md:gap-4 justify-center flex-wrap">
                <Link to="/">
                <button 
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🔙 Main Menu
                </button>
                </Link>                
                <button 
                  onClick={handleResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🗑️ Reset Progress
                </button>
                <button 
                  onClick={() => setShowInstructions(true)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  📖 How to Play
                </button>
                <button 
                  onClick={() => setShowScoringHelp(true)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🏆 Scoring System
                </button>
              </div>
            </div>
          </div>
        )}

        {gamePhase === 'playing' && (
          <div className="max-w-4xl mx-auto">
            {/* Game Stats Box with Progress Bar */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 mb-6 shadow-xl">
              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-bold text-gray-700">Progress</span>
                  <span className="text-sm font-bold text-gray-700">
                    {currentWordIndex + (gameStatus !== 'playing' ? 1 : 0)} / {currentLevelWords.length}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 border-2 border-gray-300">
                  <div 
                    className="bg-gradient-to-r from-green-400 to-emerald-500 h-full rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${((currentWordIndex + (gameStatus !== 'playing' ? 1 : 0)) / currentLevelWords.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🎯</div>
                  <p className="font-bold text-lg md:text-xl">{currentAttemptScore}</p>
                  <p className="text-xs font-bold">Score</p>
                </div>
                <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">✅</div>
                  <p className="font-bold text-lg md:text-xl">{correctAttempts}/{attempts || 1}</p>
                  <p className="text-xs font-bold">Correct</p>
                </div>
                <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🔥</div>
                  <p className="font-bold text-lg md:text-xl">{consecutiveCorrect}</p>
                  <p className="text-xs font-bold">Streak</p>
                </div>
                <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">📊</div>
                  <p className="font-bold text-lg md:text-xl">{accuracy}%</p>
                  <p className="text-xs font-bold">Accuracy</p>
                </div>
              </div>
              
              {consecutiveCorrect >= 3 && (
                <div className="mt-4 text-center bg-gradient-to-r from-orange-400/80 to-red-400/80 rounded-xl p-3">
                  <p className="text-white font-bold text-lg animate-pulse">
                    🔥 {consecutiveCorrect} in a row! You're on fire! 🔥
                  </p>
                </div>
              )}
            </div>

            {/* Main Game Area */}
            <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 mb-6 border-4 border-yellow-300">
              {currentWord ? (
                <div className="text-center">
                  {/* Hangman Display */}
                  <div className="mb-8 h-48 flex justify-center items-center">
                    <div className="w-48 h-48">
                      {HANGMAN_STAGES[Math.min(wrongGuesses, HANGMAN_STAGES.length - 1)]}
                    </div>
                  </div>

                  {/* Word Display */}
                  <div className="mb-8">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 md:mb-4">
                      {currentWord.english}
                    </h2>
                    <p className="text-3xl md:text-4xl font-bold text-purple-600 mb-4" dir="rtl" lang="ar">
                      {getDisplayedWord()}
                    </p>
                    {currentWord.pronunciation && (
                      <p className="text-gray-600 italic">
                        Pronunciation: {currentWord.pronunciation}
                      </p>
                    )}
                  </div>

                  {/* Game Status Feedback */}
                  {gameStatus === 'won' && (
                    <div className="text-2xl md:text-3xl font-bold text-green-600 mb-4 animate-bounce">
                      🎉 You guessed it!
                    </div>
                  )}
                  {gameStatus === 'lost' && (
                    <div className="text-2xl md:text-3xl font-bold text-red-600 mb-4">
                      ❌ The word was: {currentWord.arabic}
                    </div>
                  )}
                  {feedback && gameStatus === 'playing' && (
                    <div className={`text-xl md:text-2xl font-bold mb-4 ${
                      feedback === 'correct' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {feedback === 'correct' ? '✅ Correct!' : '❌ Incorrect!'}
                    </div>
                  )}

                  {/* Letter Keyboard */}
                  {gameStatus === 'playing' && (
                    <div className="grid grid-cols-6 md:grid-cols-8 gap-2 mb-6">
                      {['ا', 'أ', 'إ', 'آ', 'ب', 'ت', 'ة', 'ث', 'ج', 'ح', 'خ', 'د', 'ذ', 'ر', 'ز', 
                          'س', 'ش', 'ص', 'ض', 'ط', 'ظ', 'ع', 'غ', 'ف', 'ق', 'ك', 'ل', 'م', 'ن', 
                          'ه', 'و', 'ي', 'ى', 'ء', 'ؤ', 'ئ'
                        ].map(letter => (
                        <LetterButton
                          key={letter}
                          letter={letter}
                          onClick={handleLetterGuess}
                          disabled={guessedLetters.includes(letter)}
                        />
                      ))}
                    </div>
                  )}

                  {/* Next Word Button */}
                  {gameStatus !== 'playing' && (
                    <button
                      onClick={handleNextWord}
                      className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 border-4 border-emerald-300"
                    >
                      {currentWordIndex >= currentLevelWords.length - 1 ? "🎊 Complete Level!" : "Next Word →"}
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 md:py-12">
                  <div className="text-4xl md:text-6xl mb-4">🎭</div>
                  <div className="text-gray-600 text-lg md:text-xl font-bold">Loading next word...</div>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-xl">
              <div className="flex gap-3 md:gap-4 justify-center flex-wrap">
                <button 
                  onClick={goToMenu}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <Home size={20} />
                  Menu
                </button>
                <button 
                  onClick={startLevel}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <RotateCcw size={20} />
                  Restart Level
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Celebration Effects */}
        {Object.values(celebrations).some(Boolean) && (
          <div className="fixed inset-0 pointer-events-none z-40">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-6xl md:text-9xl animate-bounce">🎉</div>
            </div>
          </div>
        )}

        {Object.values(sparkles).some(Boolean) && (
          <div className="fixed inset-0 pointer-events-none z-40">
            {[...Array(15)].map((_, i) => (
              <div
                key={`sparkle-${i}`}
                className="absolute animate-ping text-yellow-400 text-2xl"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              >
                ✨
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      
      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-4 border-blue-300">
            <div className="p-4 md:p-8">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-blue-600">📖 How to Play Arabic Hangman!</h2>
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl md:text-3xl font-bold w-10 h-10 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 md:space-y-6 text-sm md:text-base">
                <div className="p-4 bg-blue-50 rounded-2xl border-2 border-blue-200">
                  <h3 className="font-bold text-blue-800 mb-2 flex items-center">
                    🎯 <span className="ml-2">Objective</span>
                  </h3>
                  <p className="text-blue-700">
                    Guess the Arabic word by selecting letters before the hangman drawing is completed!
                  </p>
                </div>
                
                <div className="p-4 bg-green-50 rounded-2xl border-2 border-green-200">
                  <h3 className="font-bold text-green-800 mb-2 flex items-center">
                    🎮 <span className="ml-2">How to Play</span>
                  </h3>
                  <div className="text-green-700 space-y-2">
                    <p><strong>1.</strong> You'll see the English translation of an Arabic word</p>
                    <p><strong>2.</strong> The Arabic word is shown with blanks: <span className="font-mono bg-gray-100 px-2 py-1 rounded">_ _ _ _</span></p>
                    <p><strong>3.</strong> Click on Arabic letters to guess them</p>
                    <p><strong>4.</strong> Correct letters will appear in the word</p>
                    <p><strong>5.</strong> Wrong letters add parts to the hangman!</p>
                  </div>
                </div>
                
                <div className="p-4 bg-yellow-50 rounded-2xl border-2 border-yellow-200">
                  <h3 className="font-bold text-yellow-800 mb-2 flex items-center">
                    ✨ <span className="ml-2">Special Arabic Features</span>
                  </h3>
                  <div className="text-yellow-700 space-y-2">
                    <p><strong>Smart Letter Groups:</strong> Some letters work together:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li><span className="font-bold" dir="rtl">ا أ إ آ</span> - All Alif variations</li>
                      <li><span className="font-bold" dir="rtl">ي ى</span> - Ya variations</li>
                      <li><span className="font-bold" dir="rtl">ت ة</span> - Ta variations</li>
                      <li><span className="font-bold" dir="rtl">ه ة</span> - Ha and Ta Marbuta</li>
                    </ul>
                    <p className="text-sm italic">Guessing one reveals all in the group!</p>
                  </div>
                </div>
                
                <div className="p-4 bg-red-50 rounded-2xl border-2 border-red-200">
                  <h3 className="font-bold text-red-800 mb-2 flex items-center">
                    ⚠️ <span className="ml-2">Win & Lose Conditions</span>
                  </h3>
                  <div className="text-red-700 space-y-2">
                    <p><strong>🎉 You Win:</strong> Guess all letters before hangman is complete</p>
                    <p><strong>💀 You Lose:</strong> Make 6 wrong guesses (hangman complete)</p>
                    <p><strong>🔥 Bonus:</strong> Guess words quickly for speed bonuses!</p>
                  </div>
                </div>
                
                <div className="p-4 bg-purple-50 rounded-2xl border-2 border-purple-200">
                  <h3 className="font-bold text-purple-800 mb-2 flex items-center">
                    🏆 <span className="ml-2">Tips for Success</span>
                  </h3>
                  <div className="text-purple-700 space-y-2">
                    <p><strong>1.</strong> Start with common Arabic letters like <span className="font-bold" dir="rtl">ا ل م ن ر</span></p>
                    <p><strong>2.</strong> Look for word patterns you recognize</p>
                    <p><strong>3.</strong> Remember that Arabic words often have 3-letter roots</p>
                    <p><strong>4.</strong> Use the English translation as a clue!</p>
                    <p><strong>5.</strong> Build streaks for bonus points</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 md:mt-8 text-center">
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Ready to Play! 🎭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Scoring Help Modal */}
      {showScoringHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-4 border-purple-300">
            <div className="p-4 md:p-8">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-purple-600">🏆 How Scoring Works!</h2>
                <button 
                  onClick={() => setShowScoringHelp(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl md:text-3xl font-bold w-10 h-10 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 md:space-y-6 text-sm md:text-base">
                <div className="p-4 bg-green-100 rounded-2xl border-2 border-green-300">
                  <h3 className="font-bold text-green-800 mb-2">💯 Base Points</h3>
                  <p className="text-green-700">Get {SCORING_CONSTANTS.BASE_POINTS} points for each word you guess correctly!</p>
                </div>
                
                <div className="p-4 bg-orange-100 rounded-2xl border-2 border-orange-300">
                  <h3 className="font-bold text-orange-800 mb-2">🔥 Streak Bonus</h3>
                  <p className="text-orange-700">Guess 3+ words in a row correctly to get +{SCORING_CONSTANTS.STREAK_BONUS} bonus points per word!</p>
                </div>
                
                <div className="p-4 bg-blue-100 rounded-2xl border-2 border-blue-300">
                  <h3 className="font-bold text-blue-800 mb-2">⚡ Speed Bonus</h3>
                  <p className="text-blue-700">Guess words quickly to earn up to {SCORING_CONSTANTS.SPEED_BONUS_MAX} bonus points!</p>
                </div>
                
                <div className="p-4 bg-purple-100 rounded-2xl border-2 border-purple-300">
                  <h3 className="font-bold text-purple-800 mb-2">🎯 Accuracy Bonus</h3>
                  <p className="text-purple-700">High accuracy earns up to {SCORING_CONSTANTS.ACCURACY_BONUS_MAX} bonus points!</p>
                </div>
                
                <div className="p-4 bg-yellow-100 rounded-2xl border-2 border-yellow-400">
                  <h3 className="font-bold text-yellow-800 mb-2">⭐ Stars</h3>
                  <div className="text-yellow-700 space-y-1">
                    <p>⭐ 1 Star: {SCORING_CONSTANTS.STAR_THRESHOLDS.ONE}%+ accuracy</p>
                    <p>⭐⭐ 2 Stars: {SCORING_CONSTANTS.STAR_THRESHOLDS.TWO}%+ accuracy</p>
                    <p>⭐⭐⭐ 3 Stars: {SCORING_CONSTANTS.STAR_THRESHOLDS.THREE}%+ accuracy</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 md:mt-8 text-center">
                <button 
                  onClick={() => setShowScoringHelp(false)}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Got it! 👍
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Level Complete Modal */}
      {showLevelComplete && levelCompleteData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-4 border-yellow-300">
            <div className="p-4 md:p-8 text-center">
              <div className="text-6xl md:text-8xl mb-4 md:mb-6">🎉</div>
              <h2 className="text-2xl md:text-4xl font-bold text-purple-600 mb-3 md:mb-4">
                Level {levelCompleteData.currentLevel} Complete!
              </h2>
              <p className="text-lg md:text-xl text-gray-700 mb-4 md:mb-6 font-semibold">
                {levelCompleteData.levelTitle}
              </p>
              
              <div className="mb-6 md:mb-8">
                <StarDisplay count={levelCompleteData.stars} size="text-4xl md:text-6xl" />
              </div>
              
              <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-8 text-sm md:text-base">
                <div className="bg-green-100 p-3 md:p-4 rounded-2xl border-2 border-green-300">
                  <p className="font-bold text-green-800">Total Score</p>
                  <p className="text-2xl md:text-3xl font-black text-green-800">{levelCompleteData.totalScore}</p>
                </div>
                <div className="bg-blue-100 p-3 md:p-4 rounded-2xl border-2 border-blue-300">
                  <p className="font-bold text-blue-800">Accuracy</p>
                  <p className="text-2xl md:text-3xl font-black text-blue-800">{levelCompleteData.accuracy}%</p>
                </div>
                <div className="bg-purple-100 p-3 md:p-4 rounded-2xl border-2 border-purple-300">
                  <p className="font-bold text-purple-800">Time Bonus</p>
                  <p className="text-xl md:text-2xl font-black text-purple-800">{levelCompleteData.timeBonus}</p>
                </div>
                <div className="bg-orange-100 p-3 md:p-4 rounded-2xl border-2 border-orange-300">
                  <p className="font-bold text-orange-800">Streak Bonus</p>
                  <p className="text-xl md:text-2xl font-black text-orange-800">{levelCompleteData.streakBonus}</p>
                </div>
              </div>
              
              <div className="flex gap-3 md:gap-4 justify-center flex-wrap">
                <button 
                  onClick={() => { 
                    setShowLevelComplete(false); 
                    setGamePhase('menu'); 
                  }}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🔄 Play Again
                </button>
                <button 
                  onClick={handleProceedToNextLevel}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  {levelCompleteData.isLastLevel ? "🎊 Finish Game!" : `Next Level →`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-4 md:p-8 border-4 border-red-300">
            <div className="text-center">
              <div className="text-4xl md:text-6xl mb-4">⚠️</div>
              <h2 className="text-xl md:text-2xl font-bold text-red-600 mb-3 md:mb-4">Reset All Progress?</h2>
              <p className="text-gray-700 mb-6 md:mb-8 text-sm md:text-base">
                This will delete all your scores, stars, and progress. You'll start from Level 1 again.
                This action cannot be undone!
              </p>
              <div className="flex gap-3 md:gap-4 justify-center">
                <button 
                  onClick={() => setShowResetConfirmation(false)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Yes, Reset Everything
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArabicHangman;